import { ContentType, Method } from '../../../constants/http';
import { getTokensFromDevBoxDataPlaneUri } from '../../../ids/dev-box';
import { DataResponse, FailureOperation } from '../../../models/common';
import {
    createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault,
    tryParseErrorResponse,
} from '../../../utilities/failure';
import {
    DevBoxHyperVAddOnContract,
    DevBoxTunnelAddOnContract,
    EnableDevBoxAddOnContract,
    ListDevBoxAddOnContract,
} from '../../contracts/add-ons';
import { FetchOptions, fetchRequest } from '../fetch-request';

export type ListDevBoxAddOnsResponse = DataResponse<ListDevBoxAddOnContract[]>;
export type GetDevBoxAddOnResponse = DataResponse<DevBoxHyperVAddOnContract | DevBoxTunnelAddOnContract>;
export type EnableDevBoxAddOnResponse = DataResponse<EnableDevBoxAddOnContract>;
export type CreateOrReplaceDevBoxAddOnResponse = DataResponse<DevBoxTunnelAddOnContract>;

export interface CreateOrReplaceDevBoxAddOnRequest {
    hostingResourceName?: string;
}

// AddOn type constants
export const DevBoxTunnelKind = 'devboxtunnel';

// Addons is not yet backed by a Data Plane SDK package here, so we are using `fetch` directly.
// Task 2443329: Update add-ons to use Data plane SDK package when available
export const getDevBoxAddOn = async (
    id: string,
    name: string,
    accessToken: string,
    activityId?: string
): Promise<GetDevBoxAddOnResponse> => {
    const { devBoxName, projectName, user, devCenter } = getTokensFromDevBoxDataPlaneUri(id);

    const options: FetchOptions<Response> = {
        accessToken,
        activityId,
        contentType: ContentType.ApplicationJson,
    };

    const result = await fetchRequest(
        `${devCenter}/projects/${projectName}/users/${user}/devboxes/${devBoxName}/addons/${name}?api-version=2025-04-01-preview`,
        Method.GET,
        options
    );

    // TODO: Change to using FailureResponse when the API is available in Data Plane SDK package.
    if (result.status !== 200) {
        const errorResponse = await tryParseErrorResponse(result);
        return createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
            errorResponse,
            FailureOperation.GetDevBoxAddOn
        );
    }

    return { data: await result.json(), succeeded: true };
};

// Addons is not yet backed by a Data Plane SDK package here, so we are using `fetch` directly.
// Task 2443329: Update add-ons to use Data plane SDK package when available
export const listDevBoxAddOns = async (
    id: string,
    accessToken: string,
    activityId?: string
): Promise<ListDevBoxAddOnsResponse> => {
    const { devBoxName, projectName, user, devCenter } = getTokensFromDevBoxDataPlaneUri(id);

    const options: FetchOptions<Response> = {
        accessToken,
        activityId,
        contentType: ContentType.ApplicationJson,
    };

    const result = await fetchRequest(
        `${devCenter}/projects/${projectName}/users/${user}/devboxes/${devBoxName}/addons/?api-version=2025-04-01-preview`,
        Method.GET,
        options
    );

    // TODO: Change to using FailureResponse when the API is available in Data Plane SDK package.
    if (result.status !== 200) {
        const errorResponse = await tryParseErrorResponse(result);
        return createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
            errorResponse,
            FailureOperation.ListDevBoxAddOns
        );
    }

    const addOns: { value: ListDevBoxAddOnContract[] } = await result.json();

    return { data: addOns.value, succeeded: true };
};

// Addons is not yet backed by a Data Plane SDK package here, so we are using `fetch` directly.
// Task 2443329: Update add-ons to use Data plane SDK package when available
export const enableDevBoxAddOn = async (
    id: string,
    name: string,
    accessToken: string,
    activityId?: string
): Promise<EnableDevBoxAddOnResponse> => {
    const { devBoxName, projectName, user, devCenter } = getTokensFromDevBoxDataPlaneUri(id);

    const options: FetchOptions<Response> = {
        accessToken,
        activityId,
        contentType: ContentType.ApplicationJson,
    };

    const result = await fetchRequest(
        `${devCenter}/projects/${projectName}/users/${user}/devboxes/${devBoxName}/addons/${name}:enable?api-version=2025-04-01-preview`,
        Method.POST,
        options
    );

    // TODO: Change to using FailureResponse when the API is available in Data Plane SDK package.
    if (result.status !== 200 && result.status !== 202) {
        const errorResponse = await tryParseErrorResponse(result);
        return createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
            errorResponse,
            FailureOperation.EnableDevBoxAddOn
        );
    }

    return { data: await result.json(), succeeded: true };
};

// Addons is not yet backed by a Data Plane SDK package here, so we are using `fetch` directly.
// Task 2443329: Update add-ons to use Data plane SDK package when available
export const createOrReplaceDevBoxAddOn = async (
    id: string,
    name: string,
    addOnType: string,
    body: CreateOrReplaceDevBoxAddOnRequest,
    accessToken: string,
    activityId?: string
): Promise<CreateOrReplaceDevBoxAddOnResponse> => {
    const { devBoxName, projectName, user, devCenter } = getTokensFromDevBoxDataPlaneUri(id);

    const options: FetchOptions<Response> = {
        accessToken,
        activityId,
        contentType: ContentType.ApplicationJson,
        body: JSON.stringify(body),
    };
    const result = await fetchRequest(
        `${devCenter}/projects/${projectName}/users/${user}/devboxes/${devBoxName}/addons/${name}?kind=${addOnType}&api-version=2025-04-01-preview`,
        Method.PUT,
        options
    );

    // TODO: Change to using FailureResponse when the API is available in Data Plane SDK package.
    if (result.status !== 200 && result.status !== 201) {
        const errorResponse = await tryParseErrorResponse(result);
        return createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
            errorResponse,
            FailureOperation.CreateOrReplaceDevBoxAddOn
        );
    }

    return { data: await result.json(), succeeded: true };
};

// Addons is not yet backed by a Data Plane SDK package here, so we are using `fetch` directly.
// Task 2443329: Update add-ons to use Data plane SDK package when available
export const createOrReplaceDevBoxAddOnPreview = async (
    id: string,
    name: string,
    body: CreateOrReplaceDevBoxAddOnRequest,
    accessToken: string,
    activityId?: string
): Promise<CreateOrReplaceDevBoxAddOnResponse> => {
    const { devBoxName, projectName, user, devCenter } = getTokensFromDevBoxDataPlaneUri(id);

    const options: FetchOptions<Response> = {
        accessToken,
        activityId,
        contentType: ContentType.ApplicationJson,
        body: JSON.stringify(body),
    };
    const result = await fetchRequest(
        `${devCenter}/projects/${projectName}/users/${user}/devboxes/${devBoxName}/addons/${name}?api-version=2025-04-01-preview`,
        Method.PUT,
        options
    );

    // TODO: Change to using FailureResponse when the API is available in Data Plane SDK package.
    if (result.status !== 200 && result.status !== 201) {
        const errorResponse = await tryParseErrorResponse(result);
        return createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
            errorResponse,
            FailureOperation.CreateOrReplaceDevBoxAddOn
        );
    }

    return { data: await result.json(), succeeded: true };
};
